/**
 * Les six étapes du parcours d'une demande, numérotées.
 *
 * Source unique pour la sidebar, la barre d'avancement et les pastilles de
 * filtre : le numéro affiché à ces trois endroits vient d'ici, il ne peut
 * donc pas diverger d'un écran à l'autre.
 *
 * `statuts` liste les valeurs de `statut_traitement` qui relèvent de l'étape.
 * C'est ce qui permet à chaque page de ne proposer en filtre que les statuts
 * qui la concernent — la page Devis n'a pas à offrir « Convocation envoyée ».
 * Toutes les valeurs de l'enum sont réparties ici, sans doublon : un statut
 * orphelin ne serait filtrable nulle part.
 */
export const ETAPES = [
  {
    numero: 1,
    cle: 'demande',
    label: 'Demande de devis',
    court: 'Demande',
    route: '/demande-de-devis',
    // Cycle de la demande vu depuis le devis : généré (à valider), parti au
    // client, puis les états de relance et l'issue négative.
    //
    // 'devis_envoye' et non 'devis_a_envoyer' : valider un devis déclenche
    // l'envoi dans la foulée (cf. DevisPanel.lancerValidation), la demande
    // atterrit donc directement sur « Devis envoyé ». 'devis_a_envoyer' ne
    // subsiste que si l'email échoue — un cas d'exception, suivi depuis le
    // panneau devis, pas depuis cette liste.
    //
    // Le suivi de la relance a quitté cette étape : une fois le devis parti,
    // il se poursuit sur la page Devis, qui ne liste que les devis envoyés.
    //
    // 'a_traiter' n'y figure pas volontairement : c'est l'état transitoire
    // d'une demande qui vient d'arriver et dont la génération tourne encore.
    // Rien à y faire tant qu'elle n'a pas abouti — l'échec, lui, remonte en
    // 'devis_a_generer'. Ces demandes restent dans la liste sous « Toutes »,
    // mais aucune pastille ne les isole : la somme des pastilles est donc
    // inférieure au total.
    //
    // 'termine' y figure bien qu'il soit posé tout au bout du parcours (les
    // feuilles de présence émargées au complet) : la liste des demandes est
    // le seul écran qui filtre sur statut_traitement, et un dossier clos doit
    // pouvoir s'y retrouver d'un clic.
    //
    // 'urgent' (migration 047) : posé à l'import d'une liste Trello qui le
    // mérite, il n'est jamais atteint par le parcours normal. Il se filtre
    // ici, en tête, parce que c'est précisément ce qu'on vient y chercher.
    statuts: ['urgent', 'devis_a_generer', 'traite', 'devis_envoye', 'sans_suite', 'termine'],
  },
  {
    numero: 2,
    cle: 'devis',
    label: 'Devis',
    court: 'Devis',
    route: '/demande-de-devis',
    // Même page que l'étape 1, sur sa vue « Devis » (?vue=devis).
    vue: 'devis',
    // Cette page a ses propres facettes, calculées sur l'ancienneté sans
    // réponse (cf. FACETTES_DEVIS) et non sur statut_traitement : cette liste
    // ne sert qu'à garder l'enum réparti, sans doublon.
    statuts: [
      'devis_a_envoyer',
      'a_rappeler',
      'rappel_envoye',
      'devis_accepte',
      'devis_refuse',
    ],
  },
  {
    numero: 3,
    cle: 'contrat',
    label: 'Contrat',
    court: 'Contrat',
    route: '/contrats',
    statuts: ['contract_envoye', 'contrat_signe'],
  },
  {
    numero: 4,
    cle: 'convocation',
    label: 'Convocation',
    court: 'Convocation',
    route: '/convocations',
    statuts: ['convocation_envoye'],
  },
  {
    // Le rappel de formation n'est journalisé nulle part : aucune valeur de
    // l'enum ne lui correspond. L'étape existe dans le parcours, mais elle se
    // déduit des dates de session (cf. Avancement), pas d'un statut.
    numero: 5,
    cle: 'rappel',
    label: 'Rappel',
    court: 'Rappel',
    route: '/rappels',
    statuts: [],
  },
  {
    numero: 6,
    cle: 'feuille',
    label: 'Feuille de présence',
    court: 'Feuille',
    route: '/feuilles-de-presence',
    // 'termine' est posé ici dans le parcours, mais se filtre depuis la liste
    // des demandes : il est rangé dans son étape (cf. plus haut).
    // 'deja_conclu' (migration 071) : dossier posé d'un trait, conclu
    // ailleurs — il entre ici, où il ne reste qu'à émarger.
    statuts: ['feuillepresence_envoye', 'deja_conclu'],
  },
];

/** Étape portant ce numéro. */
export const etapeParNumero = (numero) => ETAPES.find((e) => e.numero === numero);

/** Statuts relevant d'une étape, désignée par sa clé. */
export function statutsDeLEtape(cle) {
  return ETAPES.find((e) => e.cle === cle)?.statuts ?? [];
}
